/**
 * Zod schemas for the late auto-gratuity. Mirrors
 * `packages/models/src/orders/LateGratuity.ts` — SSOT
 * docs/LATE_AUTO_GRATUITY_ARCHITECTURE.md.
 *
 * Two families, as for tax exemption:
 *   - the STORED projection (`LateAutoGratuitySchema`), referenced by
 *     `OrderSchema` and `OrderDataSchema` so an order that is parsed and written
 *     back keeps it (a plain `z.object()` strips undeclared keys). The stored map
 *     is normalized by the defensive model parser first, so a malformed map reads
 *     as ABSENT instead of failing the whole order parse;
 *   - the REQUEST bodies of `/orders/:orderGuid/late-gratuity` and `/detect`.
 */

import { z } from 'zod';
import {
    LATE_GRATUITY_ACTIONS,
    LATE_GRATUITY_EVENT_ACTIONS,
    LATE_GRATUITY_REASON_MAX,
    LATE_GRATUITY_REASON_MIN,
    LATE_GRATUITY_STATUSES,
    OPERATOR_ASSERTION_MAX_LENGTH,
    parseLateAutoGratuity,
} from '@order-up/models';

import { TaxExemptionActorSchema } from './TaxExemption';

// ─── Stored shapes ──────────────────────────────────────────────────────────

const minorUnits = z.number().int().min(0);

export const LateGratuityRuleSnapshotSchema = z.object({
    type: z.enum(['percentage', 'flatAmount']),
    rate: z.number().positive().optional(),
    amountMinor: minorUnits.optional(),
    threshold: z.number().int().min(0),
    serviceTaxPercent: z.number().min(0),
    description: z.string(),
    taxRateIds: z.array(z.number().int()),
});

const LateAutoGratuityObjectSchema = z.object({
    status: z.enum(LATE_GRATUITY_STATUSES),
    owedMinor: minorUnits,
    collectedMinor: minorUnits,
    waivedMinor: minorUnits,
    partySize: z.number().int().min(0),
    detectedAt: z.coerce.date(),
    lastWaivedAt: z.coerce.date().optional(),
    ruleSnapshot: LateGratuityRuleSnapshotSchema,
    lastEventId: z.string().optional(),
    driftMinor: minorUnits.optional(),
    offCheckCollectedMinor: minorUnits.optional(),
    updatedAt: z.coerce.date(),
});

/** The stored projection. Unreadable maps normalize to `undefined`. */
export const LateAutoGratuitySchema = z.preprocess(
    (value) => parseLateAutoGratuity(value),
    LateAutoGratuityObjectSchema.optional(),
);

export const LateGratuityEventSchema = z.object({
    id: z.string(),
    action: z.enum(LATE_GRATUITY_EVENT_ACTIONS),
    amountMinor: minorUnits,
    serviceTaxMinor: minorUnits,
    allocations: z.array(z.object({ checkGuid: z.string(), gratuityMinor: minorUnits, serviceTaxMinor: minorUnits })),
    reason: z.string().max(LATE_GRATUITY_REASON_MAX).optional(),
    performedBy: TaxExemptionActorSchema.optional(),
    approvedBy: TaxExemptionActorSchema.optional(),
    at: z.coerce.date(),
    orderGuid: z.string(),
    orderNumber: z.number().optional(),
    requestFingerprint: z.string().optional(),
    statusAfter: z.enum(LATE_GRATUITY_STATUSES),
    owedAfterMinor: minorUnits,
});

// ─── Request bodies ─────────────────────────────────────────────────────────

/**
 * The idempotency key IS the event's document id, so it must be a legal one:
 * the strict-idempotency middleware's own format (16–128 of [A-Za-z0-9_-]),
 * which also rules out `/`, `.` and `__…__`.
 */
export const LateGratuityIdempotencyKeySchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{16,128}$/, 'idempotencyKey must be 16-128 letters, digits, "-" or "_"');

/**
 * `POST /orders/:orderGuid/late-gratuity`. The waiver reason's 3..200 rule is
 * checked by the ROUTE (it answers 422 with its own code); here it is only
 * bounded so an oversized body is a 400.
 */
export const LateGratuityActionBodySchema = z.object({
    tenantId: z.number().int().positive(),
    restaurantId: z.number().int().positive(),
    action: z.enum(LATE_GRATUITY_ACTIONS),
    /** Required for collect / waive (the route refuses its absence); unused by reapply. */
    expectedOwedMinor: minorUnits.optional(),
    idempotencyKey: LateGratuityIdempotencyKeySchema,
    reason: z.string().max(1000).optional(),
    requestedByEmployeeId: z.number().int().optional(),
    requestedByAssertion: z.string().max(OPERATOR_ASSERTION_MAX_LENGTH).optional(),
});
export type LateGratuityActionBody = z.infer<typeof LateGratuityActionBodySchema>;

/** `POST /orders/:orderGuid/late-gratuity/detect`. */
export const DetectLateGratuityBodySchema = z.object({
    tenantId: z.number().int().positive(),
    restaurantId: z.number().int().positive(),
});

/** A waiver's reason, trimmed: required, 3..200 characters. */
export const LateGratuityWaiverReasonSchema = z
    .string()
    .trim()
    .min(LATE_GRATUITY_REASON_MIN)
    .max(LATE_GRATUITY_REASON_MAX);

/** `GET /reports/late-gratuity-waivers`. */
export const LateGratuityWaiversQuerySchema = z.object({
    tenantId: z.coerce.number().int().positive(),
    restaurantId: z.coerce.number().int().positive(),
    startDate: z.coerce.date(),
    endDate: z.coerce.date(),
});
